import React from "react";
import { useTranslation } from "react-i18next";
import {
  Headset,
  Cloud,
  Check,
  ArrowUpRight,
  Server,
  CodeXml,
  Info,
  ChevronDown,
  Stamp,
  Clock,
  Archive,
  FileCheck2,
  ScrollText,
  ShieldCheck,
} from "lucide-react";
import Footer from "../global/Footer";

const CONTACT_URL_VI = "https://gopaperless.quantera.vn/vi/contacts";
const PRICING_URL_VI = "https://gopaperless.quantera.vn/vi/pricing/portal";

const CONTACT_URL_EN = "https://gopaperless.quantera.vn/en/contacts";
const PRICING_URL_EN = "https://gopaperless.quantera.vn/en/pricing/portal";

const DEMO_URL_VI = "https://gopaperless.quantera.vn/vi/digital-signature/";
const DEMO_URL_EN = "https://gopaperless.quantera.vn/en/digital-signature/";

export default function PricingDeploymentContent() {
  const { t, i18n } = useTranslation();

  const handleScrollToCompare = (e) => {
    e.preventDefault();
    const target = document.getElementById("compare");
    if (!target) return;
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    target.scrollIntoView({
      behavior: prefersReducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  const includedItems = [
    { Icon: Stamp, key: "item_signatures" },
    { Icon: Clock, key: "item_timestamps" },
    { Icon: Archive, key: "item_preservation" },
    { Icon: FileCheck2, key: "item_validation" },
    { Icon: ScrollText, key: "item_audit" },
    { Icon: ShieldCheck, key: "item_security" },
  ];

  const compareRows = [
    "row_system_operated",
    "row_time_to_go_live",
    "row_data_residency",
    "row_key_custody",
    "row_user_interface",
    "row_commercial_model",
  ];

  const faqItems = ["q_pricing", "q_contact", "q_integration", "q_eid"];

  return (
    <div
      id="content"
      className="pdc-root fs-navigation-overlay-content fs-navigation-overlay-left-content fs-navigation__0 no-transform overflow-hidden"
      style={{
        fontFamily: '"Montserrat", sans-serif',
        color: "#0033AD",
        backgroundColor: "#fff",
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <style>{`
        /* Font Montserrat được load 1 lần ở layout/index.html chung của app,
           không @import ở đây nữa vì @import font là global, không scope được
           theo .pdc-root nên sẽ ảnh hưởng cả header/footer ngoài trang này. */

        /* ===== shared helpers ===== */
        .pdc-root .gp-pill { border-radius: 30px; }
        .pdc-root .gp-hero-art {
          position: relative;
          overflow: hidden;
          background:
            linear-gradient(115deg, rgba(255,255,255,.10) 0 2px, transparent 2px 46px),
            linear-gradient(115deg, rgba(255,255,255,.06) 0 14px, transparent 14px 120px),
            linear-gradient(103deg, #0B2FA0 0%, #0F44C6 42%, #2C7BF2 100%);
        }
        .pdc-root .gp-soft    { box-shadow: 0 8px 30px rgb(0 51 173 / 0.06); }
        .pdc-root .gp-soft-lg { box-shadow: 0 18px 50px rgb(0 51 173 / 0.13); }
        .pdc-root .gp-lift    { transition: transform .2s ease-in-out, box-shadow .2s ease-in-out; }
        .pdc-root .gp-lift:hover { transform: translateY(-4px); box-shadow: 0 18px 50px rgb(0 51 173 / 0.13); }
        .pdc-root .gp-details[open] .gp-chev { transform: rotate(180deg); }
        .pdc-root .gp-details summary { list-style: none; cursor: pointer; }
        .pdc-root .gp-details summary::-webkit-details-marker { display: none; }

        .pdc-root .pdc-container {
          max-width: 1280px;
          margin-left: auto;
          margin-right: auto;
          padding-left: 24px;
          padding-right: 24px;
        }
        @media (min-width: 1024px) {
          .pdc-root .pdc-container { padding-left: 32px; padding-right: 32px; }
        }
        .pdc-root .pdc-section-wrap { padding-top: 80px; padding-bottom: 80px; }
        @media (min-width: 1024px) {
          .pdc-root .pdc-section-wrap { padding-top: 96px; padding-bottom: 96px; }
        }
        .pdc-root .pdc-section-title {
          font-size: 28px;
          font-weight: 800;
          letter-spacing: -0.025em;
          margin: 0;
        }
        @media (min-width: 1024px) { .pdc-root .pdc-section-title { font-size: 28px; } }
        .pdc-root .pdc-section-desc {
          margin-top: 16px;
          font-size: 16px;
          line-height: 1.625;
          color: #0033ad;
        }
        .pdc-root .pdc-section-head-center { text-align: center; max-width: 680px; margin: 0 auto 56px; }
        .pdc-root .pdc-section-head { max-width: 680px; margin-bottom: 48px; }
        .pdc-root .pdc-section-head-wide { max-width: 720px; margin-bottom: 56px; }

        /* ===== HERO ===== */
        .pdc-root .pdc-hero-inner { padding-top: 80px; padding-bottom: 80px; text-align: center; }
        @media (min-width: 1024px) {
          .pdc-root .pdc-hero-inner { padding-top: 112px; padding-bottom: 112px; }
        }
        .pdc-root .pdc-hero-eyebrow {
          color: rgba(255,255,255,.7);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          margin: 0 0 20px;
        }
        .pdc-root .pdc-hero-title {
          color: #FBC70E;
          font-weight: 800;
          letter-spacing: -0.025em;
        
          line-height: 1.05;
          margin: 0;
        }
        @media (min-width: 640px)  { .pdc-root .pdc-hero-title { font-size: 32px; } }
        @media (min-width: 1024px) { .pdc-root .pdc-hero-title { font-size: 60px; } }
        .pdc-root .pdc-hero-desc {
          margin: 24px auto 0;
          max-width: 730px;
          color: rgba(255,255,255,.85);
          font-size: 16px;
          line-height: 1.625;
        }
        @media (min-width: 1024px) { .pdc-root .pdc-hero-desc { font-size: 16px; } }
        .pdc-root .pdc-hero-actions {
          margin-top: 40px;
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          justify-content: center;
          gap: 16px;
        }
        .pdc-root .pdc-btn-gold {
          background: #FBC70E;
          padding: 14px 32px;
          font-size: 16px;
          font-weight: 600;
          transition: all .2s;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .pdc-root .pdc-btn-gold:hover { background: #E9B500; }
        .pdc-root .pdc-btn-outline-white {
          border: 2px solid rgba(255,255,255,.7);
          color: #fff;
          padding: 14px 32px;
          font-size: 16px;
          font-weight: 600;
          transition: all .2s;
        }
        .pdc-root .pdc-btn-outline-white:hover { border-color: #fff; background: rgba(255,255,255,.1); }

        /* ===== CONTACT BAND ===== */
        .pdc-root .pdc-contact-section { background: #fff; border-bottom: 1px solid #DCE7FD; }
        .pdc-root .pdc-contact-wrap { padding-top: 40px; padding-bottom: 40px; }
        .pdc-root .pdc-contact-card {
          border-radius: 16px;
          background: #EFF4FE;
          border: 1px solid #DCE7FD;
          padding: 28px 32px;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        @media (min-width: 1024px) {
          .pdc-root .pdc-contact-card { flex-direction: row; align-items: center; }
        }
        .pdc-root .pdc-contact-left { display: flex; align-items: flex-start; gap: 16px; flex: 1; }
        .pdc-root .pdc-contact-icon {
          width: 44px; height: 44px;
          border-radius: 12px;
          background: #fff;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .pdc-root .pdc-contact-title { font-size: 16px; font-weight: 700; margin: 0; }
        .pdc-root .pdc-contact-desc {
          margin: 6px 0 0;
          font-size: 14px;
          line-height: 1.625;
          color: rgba(0,51,173,.7);
          max-width: 720px;
        }
        .pdc-root .pdc-btn-brand {
          background: #0033AD; color: #fff;
          padding: 12px 28px; font-size: 16px; font-weight: 600;
          transition: all .2s; flex-shrink: 0; text-align: center;
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        }
        .pdc-root .pdc-btn-brand:hover { background: #266FEE; }

        /* ===== MODELS ===== */
        .pdc-root .pdc-models-section { background: #EFF4FE; }
        .pdc-root .pdc-models-grid { display: grid; gap: 24px; align-items: stretch; }
        @media (min-width: 1024px) {
          .pdc-root .pdc-models-grid { grid-template-columns: repeat(3, 1fr); }
        }
        .pdc-root .pdc-model-card {
          background: #fff; border-radius: 16px; border: 1px solid #DCE7FD;
          padding: 32px; display: flex; flex-direction: column;
        }
        @media (min-width: 1024px) { .pdc-root .pdc-model-card { padding: 36px; } }
        .pdc-root .pdc-model-icon {
          width: 44px; height: 44px; border-radius: 12px;
          background: #EFF4FE; display: flex; align-items: center; justify-content: center;
          margin-bottom: 24px;
        }
        .pdc-root .pdc-model-title { font-size: 24px; font-weight: 700; letter-spacing: -0.025em; margin: 0; }
        .pdc-root .pdc-model-tagline { margin: 8px 0 0; font-size: 14px; font-weight: 600; color: #266FEE; }
        .pdc-root .pdc-model-desc { margin: 16px 0 0; font-size: 14px; line-height: 1.625; color: rgba(0,51,173,.7); }
        .pdc-root .pdc-model-features {
          margin-top: 28px; display: flex; flex-direction: column; gap: 14px;
          font-size: 14px; line-height: 1.375; flex: 1; list-style: none; padding: 0;
        }
        .pdc-root .pdc-model-feature { display: flex; gap: 12px; }
        .pdc-root .pdc-model-feature svg { margin-top: 2px; flex-shrink: 0; color: #266FEE; }
        .pdc-root .pdc-model-cta {
          margin-top: 32px; border: 2px solid #0033AD; color: #0033AD;
          padding: 12px 24px; font-size: 14px; font-weight: 600; text-align: center;
          transition: all .2s; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        }
        .pdc-root .pdc-model-cta:hover { background: #0033AD; color: #fff; }
        .pdc-root .pdc-model-cta-note { margin: 12px 0 0; font-size: 12px; color: rgba(0,51,173,.5); text-align: center; }

        .pdc-root .pdc-model-card--featured { position: relative; background: #0033AD; color: #fff; }
        @media (min-width: 1024px) {
          .pdc-root .pdc-model-card--featured { margin-top: -16px; margin-bottom: -16px; }
        }
        .pdc-root .pdc-model-badge {
          position: absolute; top: -12px; left: 32px;
          background: #FBC70E; color: #0033AD;
          font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
          padding: 6px 16px;
        }
        .pdc-root .pdc-model-card--featured .pdc-model-icon { background: rgba(255,255,255,.1); }
        .pdc-root .pdc-model-card--featured .pdc-model-tagline { color: #FBC70E; }
        .pdc-root .pdc-model-card--featured .pdc-model-desc { color: rgba(255,255,255,.75); }
        .pdc-root .pdc-model-card--featured .pdc-model-feature svg { color: #FBC70E; }
        .pdc-root .pdc-model-cta--gold {
          margin-top: 32px; background: #FBC70E; color: #0033AD;
          padding: 12px 24px; font-size: 14px; font-weight: 600; text-align: center;
          transition: all .2s; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        }
        .pdc-root .pdc-model-cta--gold:hover { background: #E9B500; }
        .pdc-root .pdc-model-card--featured .pdc-model-cta-note { color: rgba(255,255,255,.5); }

        .pdc-root .pdc-models-note {
          margin-top: 40px; border-radius: 16px; background: #fff; border: 1px solid #DCE7FD;
          padding: 24px 28px; display: flex; gap: 16px; align-items: flex-start;
        }
        .pdc-root .pdc-models-note svg { flex-shrink: 0; margin-top: 2px; color: #266FEE; }
        .pdc-root .pdc-models-note p { font-size: 14px; line-height: 1.625; color: rgba(0,51,173,.7); margin: 0; }

        /* ===== COMPARE TABLE ===== */
        .pdc-root .pdc-compare-section { background: #fff; scroll-margin-top: 96px; }
        .pdc-root .pdc-table-wrap { overflow-x: auto; border-radius: 16px; border: 1px solid #DCE7FD; }
        .pdc-root .pdc-table { width: 100%; min-width: 880px; text-align: left; border-collapse: collapse; }
        .pdc-root .pdc-table thead tr { background: #EFF4FE; }
        .pdc-root .pdc-table th, .pdc-root .pdc-table td { padding: 20px 28px; }
        .pdc-root .pdc-table-th-label { width: 24%; }
        .pdc-root .pdc-table-th-col {
          font-size: 14px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;
          color: rgba(0,51,173,.45);
        }
        .pdc-root .pdc-table-th-col--highlight { color: #266FEE; background: rgba(220,231,253,.5); }
        .pdc-root .pdc-table tbody { font-size: 14px; }
        .pdc-root .pdc-table tbody tr { border-top: 1px solid #DCE7FD; transition: background .2s; }
        .pdc-root .pdc-table tbody tr:hover { background: rgba(239,244,254,.6); }
        .pdc-root .pdc-table .pdc-row-label { font-weight: 600; }
        .pdc-root .pdc-table td { color: rgba(0,51,173,.7); }
        .pdc-root .pdc-table td.pdc-td-highlight { background: rgba(239,244,254,.6); }
        .pdc-root .pdc-table-pill {
          display: inline-block;
          background: #0033AD; color: #fff; font-size: 14px; font-weight: 600;
          padding: 6px 14px;
        }

        /* ===== INCLUDED ===== */
        .pdc-root .pdc-included-section { background: #EFF4FE; }
        .pdc-root .pdc-included-grid { display: grid; gap: 20px; }
        @media (min-width: 640px)  { .pdc-root .pdc-included-grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 1024px) { .pdc-root .pdc-included-grid { grid-template-columns: repeat(3, 1fr); } }
        .pdc-root .pdc-included-card { background: #fff; border-radius: 16px; border: 1px solid #DCE7FD; padding: 28px; }
        .pdc-root .pdc-included-head { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
        .pdc-root .pdc-included-head svg { color: #266FEE; }
        .pdc-root .pdc-included-head h3 { font-size: 16px; font-weight: 700; margin: 0; }
        .pdc-root .pdc-included-body { font-size: 14px; line-height: 1.625; color: rgba(0,51,173,.65); margin: 0; }

        /* ===== FAQ ===== */
        .pdc-root .pdc-faq-section { background: #fff; }
        .pdc-root .pdc-faq-wrap { max-width: 860px; margin: 0 auto; padding: 80px 24px; }
        @media (min-width: 1024px) {
          .pdc-root .pdc-faq-wrap { padding-top: 96px; padding-bottom: 96px; padding-left: 32px; padding-right: 32px; }
        }
        .pdc-root .pdc-faq-title {
          font-size: 30px; font-weight: 800; letter-spacing: -0.025em;
          margin: 0 0 48px; text-align: center;
        }
        @media (min-width: 1024px) { .pdc-root .pdc-faq-title { font-size: 28px; } }
        .pdc-root .pdc-faq-list { display: flex; flex-direction: column; gap: 16px; }
        .pdc-root .pdc-faq-item { border-radius: 16px; border: 1px solid #DCE7FD; background: #fff; overflow: hidden; }
        .pdc-root .pdc-faq-summary {
          display: flex; align-items: center; justify-content: space-between; gap: 24px;
          padding: 24px 28px;
        }
        .pdc-root .pdc-faq-question { font-size: 16px; font-weight: 700; padding-right: 16px; }
        .pdc-root .pdc-faq-summary svg { flex-shrink: 0; color: #266FEE; transition: transform .2s; }
        .pdc-root .pdc-faq-answer {
          padding: 0 28px 28px; margin-top: -4px;
          font-size: 14px; line-height: 1.625; color: rgba(0,51,173,.7);
        }

        /* ===== CTA BAND ===== */
        .pdc-root .pdc-cta-section { background: #fff; scroll-margin-top: 96px; }
        .pdc-root .pdc-cta-wrap { padding-bottom: 80px; }
        @media (min-width: 1024px) { .pdc-root .pdc-cta-wrap { padding-bottom: 96px; } }
        .pdc-root .pdc-cta-card {
          border-radius: 24px; padding: 56px 32px;
          display: flex; flex-direction: column; justify-content: space-between; gap: 40px;
        }
        @media (min-width: 1024px) {
          .pdc-root .pdc-cta-card { flex-direction: row; align-items: center; padding: 64px 56px; }
        }
        .pdc-root .pdc-cta-text { max-width: 580px; }
        .pdc-root .pdc-cta-title { color: #fff; font-size: 28px; font-weight: 800; letter-spacing: -0.025em; margin: 0; }
        @media (min-width: 1024px) { .pdc-root .pdc-cta-title { font-size: 28px; } }
        .pdc-root .pdc-cta-desc { margin: 16px 0 0; color: rgba(255,255,255,.8); font-size: 16px; line-height: 1.625; }
        .pdc-root .pdc-cta-actions { display: flex; flex-wrap: wrap; gap: 16px; flex-shrink: 0; }
      `}</style>

      {/* ============================================================
          HERO
          ============================================================ */}
      <section className="gp-hero-art">
        <div className="pdc-container pdc-hero-inner">
          <p className="pdc-hero-eyebrow">
            {t("pricing_deployment.hero.eyebrow")}
          </p>
          <h1 className="pdc-hero-title">
            {t("pricing_deployment.hero.title")}
          </h1>
          <p className="pdc-hero-desc">
            {t("pricing_deployment.hero.description")}
          </p>
          <div className="pdc-hero-actions">
            <a
              href={i18n.language === "en" ? PRICING_URL_EN : PRICING_URL_VI}
              target="_blank"
              rel="noopener noreferrer"
              className="gp-pill gp-soft-lg pdc-btn-gold"
            >
              <span>{t("pricing_deployment.hero.cta_primary")}</span>
              <ArrowUpRight size={16} />
            </a>
            <a
              href="#compare"
              onClick={handleScrollToCompare}
              className="gp-pill pdc-btn-outline-white"
            >
              {t("pricing_deployment.hero.cta_secondary")}
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================
          SINGLE POINT OF CONTACT BAND
          ============================================================ */}
      <section className="pdc-contact-section">
        <div className="pdc-container pdc-contact-wrap">
          <div className="pdc-contact-card">
            <div className="pdc-contact-left">
              <div className="pdc-contact-icon gp-soft">
                <Headset size={22} color="#266FEE" />
              </div>
              <div>
                <h2 className="pdc-contact-title">
                  {t("pricing_deployment.contact_band.title")}
                </h2>
                <p className="pdc-contact-desc">
                  {t("pricing_deployment.contact_band.description")}
                </p>
              </div>
            </div>
            <a
              href={i18n.language === "en" ? CONTACT_URL_EN : CONTACT_URL_VI}
              target="_blank"
              rel="noopener noreferrer"
              className="gp-pill pdc-btn-brand"
            >
              <span>{t("pricing_deployment.contact_band.cta")}</span>
              <ArrowUpRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================
          THREE DELIVERY MODELS
          ============================================================ */}
      <section className="pdc-models-section">
        <div className="pdc-container pdc-section-wrap">
          <div className="pdc-section-head-center">
            <h2 className="pdc-section-title">
              {t("pricing_deployment.models.heading")}
            </h2>
            <p className="pdc-section-desc">
              {t("pricing_deployment.models.description")}
            </p>
          </div>

          <div className="pdc-models-grid">
            {/* Card 1 — Cloud */}
            <article className="pdc-model-card gp-soft gp-lift">
              <div className="pdc-model-icon">
                <Cloud size={22} color="#266FEE" />
              </div>
              <h3 className="pdc-model-title">
                {t("pricing_deployment.models.cloud.title")}
              </h3>
              <p className="pdc-model-tagline" style={{ lineHeight: "1.625" }}>
                {t("pricing_deployment.models.cloud.tagline")}
              </p>
              <p className="pdc-model-desc">
                {t("pricing_deployment.models.cloud.description")}
              </p>
              <ul className="pdc-model-features">
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.cloud.feature_1")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.cloud.feature_2")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.cloud.feature_3")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.cloud.feature_4")}</span>
                </li>
              </ul>
              <a
                href={i18n.language === "en" ? PRICING_URL_EN : PRICING_URL_VI}
                target="_blank"
                rel="noopener noreferrer"
                className="gp-pill pdc-model-cta"
              >
                <span>{t("pricing_deployment.models.cloud.cta")}</span>
                <ArrowUpRight size={16} />
              </a>
              <p className="pdc-model-cta-note">
                {t("pricing_deployment.models.cloud.cta_note")}
              </p>
            </article>

            {/* Card 2 — On-Premise (featured) */}
            <article className="pdc-model-card pdc-model-card--featured gp-soft-lg">
              <span className="gp-pill pdc-model-badge">
                {t("pricing_deployment.models.onprem.badge")}
              </span>
              <div className="pdc-model-icon">
                <Server size={22} color="#FBC70E" />
              </div>
              <h3 className="pdc-model-title" style={{ color: "#FFFFFF" }}>
                {t("pricing_deployment.models.onprem.title")}
              </h3>
              <p className="pdc-model-tagline" style={{ lineHeight: "1.625" }}>
                {t("pricing_deployment.models.onprem.tagline")}
              </p>
              <p className="pdc-model-desc">
                {t("pricing_deployment.models.onprem.description")}
              </p>
              <ul className="pdc-model-features">
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.onprem.feature_1")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.onprem.feature_2")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.onprem.feature_3")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.onprem.feature_4")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.onprem.feature_5")}</span>
                </li>
              </ul>
              <a
                href={i18n.language === "en" ? CONTACT_URL_EN : CONTACT_URL_VI}
                target="_blank"
                rel="noopener noreferrer"
                className="gp-pill pdc-model-cta--gold"
              >
                <span>{t("pricing_deployment.models.onprem.cta")}</span>
                <ArrowUpRight size={16} />
              </a>
              <p className="pdc-model-cta-note">
                {t("pricing_deployment.models.onprem.cta_note")}
              </p>
            </article>

            {/* Card 3 — OEM / API */}
            <article className="pdc-model-card gp-soft gp-lift">
              <div className="pdc-model-icon">
                <CodeXml size={22} color="#266FEE" />
              </div>
              <h3 className="pdc-model-title">
                {t("pricing_deployment.models.oem.title")}
              </h3>
              <p className="pdc-model-tagline" style={{ lineHeight: "1.625" }}>
                {t("pricing_deployment.models.oem.tagline")}
              </p>
              <p className="pdc-model-desc">
                {t("pricing_deployment.models.oem.description")}
              </p>
              <ul className="pdc-model-features">
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.oem.feature_1")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.oem.feature_2")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.oem.feature_3")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.oem.feature_4")}</span>
                </li>
                <li className="pdc-model-feature">
                  <Check size={16} />
                  <span>{t("pricing_deployment.models.oem.feature_5")}</span>
                </li>
              </ul>
              <a
                href={i18n.language === "en" ? CONTACT_URL_EN : CONTACT_URL_VI}
                target="_blank"
                rel="noopener noreferrer"
                className="gp-pill pdc-model-cta"
              >
                <span>{t("pricing_deployment.models.oem.cta")}</span>
                <ArrowUpRight size={16} />
              </a>
              <p className="pdc-model-cta-note">
                {t("pricing_deployment.models.oem.cta_note")}
              </p>
            </article>
          </div>

          <div className="pdc-models-note gp-soft">
            <Info size={20} color="#266FEE" />
            <p>{t("pricing_deployment.models.note")}</p>
          </div>
        </div>
      </section>

      {/* ============================================================
          COMPARISON TABLE
          ============================================================ */}
      <section id="compare" className="pdc-compare-section">
        <div className="pdc-container pdc-section-wrap">
          <div className="pdc-section-head">
            <h2 className="pdc-section-title">
              {t("pricing_deployment.compare.heading")}
            </h2>
            <p className="pdc-section-desc">
              {t("pricing_deployment.compare.description")}
            </p>
          </div>

          <div className="pdc-table-wrap gp-soft">
            <table className="pdc-table">
              <thead>
                <tr>
                  <th className="pdc-table-th-label" />
                  <th className="pdc-table-th-col">
                    {t("pricing_deployment.compare.col_cloud")}
                  </th>
                  <th className="pdc-table-th-col pdc-table-th-col--highlight">
                    {t("pricing_deployment.compare.col_onprem")}
                  </th>
                  <th className="pdc-table-th-col">
                    {t("pricing_deployment.compare.col_oem")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map((key) => (
                  <tr key={key}>
                    <th className="pdc-row-label">
                      {t(`pricing_deployment.compare.${key}.label`)}
                    </th>
                    <td>{t(`pricing_deployment.compare.${key}.cloud`)}</td>
                    <td className="pdc-td-highlight">
                      {t(`pricing_deployment.compare.${key}.onprem`)}
                    </td>
                    <td>{t(`pricing_deployment.compare.${key}.oem`)}</td>
                  </tr>
                ))}
                <tr>
                  <th className="pdc-row-label">
                    {t("pricing_deployment.compare.row_point_of_contact.label")}
                  </th>
                  <td>
                    <span className="gp-pill pdc-table-pill">
                      {t(
                        "pricing_deployment.compare.row_point_of_contact.value",
                      )}
                    </span>
                  </td>
                  <td className="pdc-td-highlight">
                    <span className="gp-pill pdc-table-pill">
                      {t(
                        "pricing_deployment.compare.row_point_of_contact.value",
                      )}
                    </span>
                  </td>
                  <td>
                    <span className="gp-pill pdc-table-pill">
                      {t(
                        "pricing_deployment.compare.row_point_of_contact.value",
                      )}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============================================================
          INCLUDED IN EVERY MODEL
          ============================================================ */}
      <section className="pdc-included-section">
        <div className="pdc-container pdc-section-wrap">
          <div className="pdc-section-head-wide">
            <h2 className="pdc-section-title">
              {t("pricing_deployment.included.heading")}
            </h2>
            <p className="pdc-section-desc">
              {t("pricing_deployment.included.description")}
            </p>
          </div>

          <div className="pdc-included-grid">
            {includedItems.map(({ Icon, key }) => (
              <div key={key} className="pdc-included-card gp-soft gp-lift">
                <div className="pdc-included-head">
                  <Icon size={20} />
                  <h3>{t(`pricing_deployment.included.${key}.title`)}</h3>
                </div>
                <p className="pdc-included-body">
                  {t(`pricing_deployment.included.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          FAQ
          ============================================================ */}
      <section className="pdc-faq-section">
        <div className="pdc-faq-wrap">
          <h2 className="pdc-faq-title">
            {t("pricing_deployment.faq.heading")}
          </h2>

          <div className="pdc-faq-list">
            {faqItems.map((key, i) => (
              <details
                key={key}
                className="gp-details pdc-faq-item gp-soft"
                open={i === 0}
              >
                <summary className="pdc-faq-summary">
                  <span className="pdc-faq-question">
                    {t(`pricing_deployment.faq.${key}.question`)}
                  </span>
                  <ChevronDown className="gp-chev" size={20} />
                </summary>
                <p className="pdc-faq-answer">
                  {t(`pricing_deployment.faq.${key}.answer`)}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          CTA BAND
          ============================================================ */}
      <section id="contact" className="pdc-cta-section">
        <div className="pdc-container pdc-cta-wrap">
          <div className="gp-hero-art pdc-cta-card">
            <div className="pdc-cta-text">
              <h2 className="pdc-cta-title">
                {t("pricing_deployment.cta.title")}
              </h2>
              <p className="pdc-cta-desc">
                {t("pricing_deployment.cta.description")}
              </p>
            </div>
            <div className="pdc-cta-actions">
              <a
                href={i18n.language === "en" ? CONTACT_URL_EN : CONTACT_URL_VI}
                target="_blank"
                rel="noopener noreferrer"
                className="gp-pill pdc-btn-gold"
              >
                <span>{t("pricing_deployment.cta.primary")}</span>
                <ArrowUpRight size={16} />
              </a>
              <a
                href={i18n.language === "en" ? DEMO_URL_EN : DEMO_URL_VI}
                target="_blank"
                rel="noopener noreferrer"
                className="gp-pill pdc-btn-outline-white"
              >
                {t("pricing_deployment.cta.secondary")}
              </a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
